import { memo, useState } from "react";
import PropTypes from "prop-types";
import "./DepartureDateSidePanelContent.scss";
import Button, { BUTTON_SIZES, BUTTON_VARIANTS } from "app/pages/.shared/form/Button";
import CalendarList from "app/pages/.shared/CalendarList/CalendarList";
import { FormattedMessage } from "react-intl";
import IconClose from "app/pages/.shared/IconClose";
import { getMonthsYearsInDateInterval } from "app/utils/utils";
import { compact } from "lodash";
import className from "classnames";
import IconCloseFull from "app/pages/.shared/IconCloseFull";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import DepartureDateSidePanelSelectedDate from "app/pages/SmartDP/Search/DepartureDateSidePanelContent/DepartureDateSidePanelSelectedDate";

const DepartureDateSidePanelContent = ({
	initialDate,
	onDateSelect,
	onDateConfirm,
	onClose,
	departureDateMin,
	departureDateMax,
	checkDateAvailability,
	isCalendarDisabled,
	displayCalendarIcon,
	calendarDisabledView,
	departureDateLabel,
	returnDateLabel,
}) => {
	const [selectedDate, setSelectedDate] = useState(compact(initialDate));

	const handleDateSelect = date => {
		let newDate;
		// if selectedDate(departDate, endDate) exists, reset selectedDate
		if (selectedDate.length >= 2) {
			newDate = [].concat(date);
		} else {
			newDate = [...selectedDate, date];
		}
		setSelectedDate(newDate);
		onDateSelect(newDate);
	};

	const handleDateConfirmation = () => {
		onDateConfirm(selectedDate);
	};

	let months = [];
	if (departureDateMin && departureDateMax) {
		months = getMonthsYearsInDateInterval(departureDateMin, departureDateMax);
	}

	const handleCleanDepartureDate = () => {
		setSelectedDate([]);
		onDateSelect([]);
	};

	const departureDate = selectedDate[0];
	const endDate = selectedDate[1];

	const handleCleanArrivalDate = () => {
		const newDate = [].concat(departureDate);
		setSelectedDate(newDate);
		onDateSelect(newDate);
	};

	return (
		<div className="departure-date-side-panel-content">
			<header className="departure-date-side-panel-content__header">
				<div className="departure-date-side-panel-content__header-title">
					<Typography variant={TYPOGRAPHY_VARIANTS.XL} isBold>
						<FormattedMessage id="sdp.search.departure.date.input.label" />
					</Typography>
					<div
						onClick={onClose}
						className="departure-date-side-panel-content__header-icon"
					>
						<IconClose width={24} height={24} />
					</div>
				</div>
				<div className="departure-date-side-panel-content__dates-selected">
					<div
						className={className({
							"departure-date-side-panel-content__date-selected": true,
							"departure-date-side-panel-content__date-selected--has-value":
								departureDate || (!departureDate && !endDate),
						})}
					>
						<div>
							<p className="departure-date-side-panel-content__date-selected-label">
								{departureDateLabel}
							</p>

							{departureDate ? (
								<p className="departure-date-side-panel-content__date-selected-value">
									<DepartureDateSidePanelSelectedDate date={departureDate} />
								</p>
							) : (
								<Typography
									className="departure-date-side-panel-content__date-selected-value"
									variant={TYPOGRAPHY_VARIANTS.REGULAR}
								>
									<FormattedMessage id="sdp.search.departure.date.input.placeholder" />
								</Typography>
							)}
						</div>
						{departureDate && (
							<div
								className="departure-date-side-panel-content__date-selected-clear-icon"
								onClick={handleCleanDepartureDate}
							>
								<IconCloseFull />
							</div>
						)}
					</div>
					<div
						className={className({
							"departure-date-side-panel-content__date-selected": true,
							"departure-date-side-panel-content__date-selected--has-value":
								endDate || (departureDate && !endDate),
							"departure-date-side-panel-content__date-selected--disabled": !departureDate,
						})}
					>
						<div>
							<p className="departure-date-side-panel-content__date-selected-label">
								{returnDateLabel}
							</p>
							{endDate && (
								<p className="departure-date-side-panel-content__date-selected-value">
									<DepartureDateSidePanelSelectedDate date={endDate} />
								</p>
							)}
							{Boolean(departureDate) && !endDate && (
								<Typography
									className="departure-date-side-panel-content__date-selected-value"
									variant={TYPOGRAPHY_VARIANTS.REGULAR}
								>
									<FormattedMessage id="sdp.search.return.date.input.placeholder" />
								</Typography>
							)}
						</div>
						{endDate && (
							<div
								className="departure-date-side-panel-content__date-selected-clear-icon"
								onClick={handleCleanArrivalDate}
							>
								<IconCloseFull />
							</div>
						)}
					</div>
				</div>
			</header>
			{isCalendarDisabled ? (
				calendarDisabledView
			) : (
				<div className="departure-date-side-panel-content__body">
					<CalendarList
						heightOffset={250} // header + footer with CTA + selection overview
						selectedDate={selectedDate}
						months={months}
						onDateSelect={handleDateSelect}
						departureDateMin={departureDateMin}
						departureDateMax={departureDateMax}
						checkDateAvailability={checkDateAvailability}
						displayCalendarIcon={displayCalendarIcon}
					/>
				</div>
			)}
			<footer className="departure-date-side-panel-content__footer">
				{!isCalendarDisabled && (
					<Button
						variant={BUTTON_VARIANTS.FILLED}
						size={BUTTON_SIZES.LARGE}
						disabled={selectedDate.length !== 2}
						onClick={handleDateConfirmation}
					>
						<FormattedMessage id="sdp.search.departure.date.side.panel.cta" />
					</Button>
				)}
			</footer>
		</div>
	);
};

DepartureDateSidePanelContent.propTypes = {
	onDateSelect: PropTypes.func,
	onDateConfirm: PropTypes.func,
	initialDate: PropTypes.array,
	onClose: PropTypes.func,
	departureDateMin: PropTypes.number,
	departureDateMax: PropTypes.number,
	checkDateAvailability: PropTypes.func,
	isCalendarDisabled: PropTypes.bool,
	calendarDisabledView: PropTypes.element,
	departureDateLabel: PropTypes.object,
	returnDateLabel: PropTypes.object,
	displayCalendarIcon: PropTypes.bool,
};

export default memo(DepartureDateSidePanelContent);
